import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowSquareOut, CalendarBlank, CreditCard, FileText, WarningCircle } from "@phosphor-icons/react";
import { arrayUnion, collection, doc, serverTimestamp, writeBatch } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { getBytes, getDownloadURL, ref as storageRef } from "firebase/storage";
import { AppShell } from "@/components/layout/AppShell";
import { Button, Spinner } from "@/components/ui";
import { useAuthContext } from "@/contexts/AuthContext";
import { db, functions, storage } from "@/config/firebase";
import { DEMO_PAYMENT_BYPASS_EMAIL, isDemoPaymentBypassEmail } from "@/lib/demoPaymentBypass";
import { useCatalogEntry } from "@/hooks/useCatalogEntry";
import { useChapters } from "@/hooks/useChapters";
import { usePipelineStages } from "@/hooks/usePipelineStages";
import { useProject } from "@/hooks/useProject";
import { useStateLog } from "@/hooks/useStateLog";
import { BookCover, PressEyebrow, PressPanel, StatusPill } from "@/components/pullpress/PressVisuals";
import type {
  ArtifactRef,
  ArtifactType,
  CatalogEntry,
  Chapter,
  PipelineStage,
  PipelineStageName,
  Project,
  StageStatus,
  StateMachineState,
  StateTransitionLog,
  VerificationResult,
} from "@/types";

type PipelineDefinition = {
  readonly stage: PipelineStageName;
  readonly label: string;
};

type DisplayArtifactType = Extract<
  ArtifactType,
  | "draft_docx"
  | "revised_docx"
  | "editorial_output"
  | "editorial_report"
  | "continuity_report"
  | "interior_pdf"
  | "cover_pdf"
  | "epub"
  | "onix_xml"
  | "amazon_listing"
  | "metadata_json"
  | "kdp_export"
  | "ingramspark_bundle"
>;

type PacketArtifact = {
  readonly key: string;
  readonly artifact: ArtifactRef;
  readonly stage: PipelineStage;
  readonly type: DisplayArtifactType;
};

type ProductionMetadata = {
  readonly title?: string;
  readonly subtitle?: string;
  readonly author_name?: string;
  readonly isbn?: string;
  readonly trim_size?: string;
  readonly paper_stock?: string;
  readonly page_count?: number;
  readonly word_count?: number;
  readonly chapter_count?: number;
  readonly spine_width_inches?: number;
  readonly cover_width_inches?: number;
  readonly cover_height_inches?: number;
  readonly retail_price_usd?: number;
  readonly bisac_codes?: readonly string[];
  readonly keywords?: readonly string[];
  readonly publication_date?: string;
  readonly description?: string;
};

type ProductionAction = {
  readonly title: string;
  readonly body: string;
  readonly buttonLabel: string;
  readonly disabled: boolean;
};

const PIPELINE_DEFINITIONS: readonly PipelineDefinition[] = [
  { stage: "ingestion", label: "Source" },
  { stage: "transcription", label: "Voice" },
  { stage: "theme_extraction", label: "Shape" },
  { stage: "story_architecture", label: "Map" },
  { stage: "draft_generation", label: "Manuscript" },
  { stage: "review", label: "Proof" },
  { stage: "editorial", label: "Edit" },
  { stage: "packaging", label: "Packet" },
  { stage: "publishing", label: "Release" },
];

const ARTIFACT_COPY: Record<DisplayArtifactType, { readonly name: string; readonly purpose: string }> = {
  draft_docx: { name: "Proof DOCX", purpose: "Manuscript ready for author approval" },
  revised_docx: { name: "Revised DOCX", purpose: "Approved revision brought back into the run" },
  editorial_output: { name: "Edited manuscript", purpose: "Manuscript after the editorial pass" },
  editorial_report: { name: "Editorial report", purpose: "Change notes and quality findings" },
  continuity_report: { name: "Continuity report", purpose: "Narrative consistency checks" },
  interior_pdf: { name: "Interior PDF", purpose: "Print-ready interior" },
  cover_pdf: { name: "Cover PDF", purpose: "Print-ready wraparound cover" },
  epub: { name: "EPUB", purpose: "Digital book release file" },
  onix_xml: { name: "ONIX XML", purpose: "Retail metadata handoff" },
  amazon_listing: { name: "Amazon listing", purpose: "KDP listing copy" },
  metadata_json: { name: "Metadata JSON", purpose: "Book details for the release packet" },
  kdp_export: { name: "KDP export", purpose: "Guided Amazon handoff packet" },
  ingramspark_bundle: { name: "IngramSpark bundle", purpose: "Operator print handoff packet" },
};

const CHANNEL_LABELS: Record<CatalogEntry["distribution_channels"][number], string> = {
  amazon_kdp: "Amazon KDP",
  ingramspark: "IngramSpark",
  draft2digital: "Draft2Digital",
  storyforge_direct: "Direct reader",
};

const STATE_READINESS: Record<StateMachineState, number> = {
  CREATED: 0,
  INGESTED: 8,
  TRANSCRIBING: 12,
  TRANSCRIBED: 18,
  ANALYZING: 24,
  ANALYZED: 30,
  STRUCTURING: 36,
  STRUCTURED: 42,
  DRAFTING: 48,
  DRAFTED: 56,
  AWAITING_REVIEW: 60,
  REVISED: 66,
  AUTO_ADVANCED: 66,
  EDITING: 72,
  EDITED: 80,
  PACKAGING: 88,
  PACKAGED: 94,
  PUBLISHING: 97,
  PUBLISHED: 100,
  ERROR: 0,
};

export function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const { user } = useAuthContext();
  const { project, loading: projectLoading } = useProject(projectId ?? null);
  const pipelineState = usePipelineStages(projectId ?? null);
  const chapterState = useChapters(projectId ?? null);
  const stateLogState = useStateLog(projectId ?? null);
  const catalogState = useCatalogEntry(projectId ?? null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [metadata, setMetadata] = useState<ProductionMetadata | null>(null);
  const [metadataError, setMetadataError] = useState<string | null>(null);
  const [artifactUrls, setArtifactUrls] = useState<Record<string, string>>({});
  const [artifactUrlError, setArtifactUrlError] = useState<string | null>(null);

  const latestStages = useMemo(
    () => buildLatestStageMap(pipelineState.stages),
    [pipelineState.stages],
  );
  const packetArtifacts = useMemo(
    () => buildPacketArtifacts(pipelineState.stages),
    [pipelineState.stages],
  );
  const packetArtifactPaths = useMemo(
    () => packetArtifacts.map((packet) => `${packet.key}:${packet.artifact.storage_path}`).join("|"),
    [packetArtifacts],
  );
  const metadataArtifact = useMemo(
    () => packetArtifacts.find((packet) => packet.type === "metadata_json") ?? null,
    [packetArtifacts],
  );
  const metadataPath = metadataArtifact?.artifact.storage_path ?? null;
  const verificationResults = useMemo(
    () => buildVerificationResults(pipelineState.stages),
    [pipelineState.stages],
  );
  const warnings = useMemo(
    () => verificationResults.filter((result) => !result.passed && !result.blocking),
    [verificationResults],
  );
  const blockingFailures = useMemo(
    () => verificationResults.filter((result) => !result.passed && result.blocking),
    [verificationResults],
  );

  useEffect(() => {
    if (!metadataPath) {
      setMetadata(null);
      setMetadataError(null);
      return;
    }

    let cancelled = false;
    setMetadataError(null);
    void getBytesJson(metadataPath)
      .then((value) => {
        if (cancelled) return;
        setMetadata(parseProductionMetadata(value));
      })
      .catch((err) => {
        if (cancelled) return;
        setMetadata(null);
        setMetadataError(err instanceof Error ? err.message : "Could not load production metadata.");
      });

    return () => {
      cancelled = true;
    };
  }, [metadataPath]);

  useEffect(() => {
    if (packetArtifacts.length === 0) {
      setArtifactUrls({});
      setArtifactUrlError(null);
      return;
    }

    let cancelled = false;
    setArtifactUrlError(null);
    void Promise.all(
      packetArtifacts.map(async (packet) => {
        try {
          const url = await getDownloadURL(storageRef(storage, artifactDownloadPath(packet)));
          return { key: packet.key, url, error: null } as const;
        } catch (err) {
          const message = err instanceof Error ? err.message : "Artifact link unavailable.";
          return { key: packet.key, url: null, error: message } as const;
        }
      }),
    )
      .then((results) => {
        if (cancelled) return;
        const urls: Record<string, string> = {};
        for (const result of results) {
          if (result.url !== null) urls[result.key] = result.url;
        }
        setArtifactUrls(urls);
        const failures = results.filter((result) => result.error);
        setArtifactUrlError(failures.length > 0 ? `${failures.length} artifact link${failures.length === 1 ? "" : "s"} unavailable.` : null);
      })
      .catch((err) => {
        if (cancelled) return;
        setArtifactUrlError(err instanceof Error ? err.message : "Could not load artifact links.");
      });

    return () => {
      cancelled = true;
    };
  }, [packetArtifactPaths, packetArtifacts]);

  if (projectLoading) {
    return (
      <AppShell variant="auth">
        <div className="flex justify-center py-20">
          <Spinner />
        </div>
      </AppShell>
    );
  }

  if (!project || !projectId) {
    return (
      <AppShell variant="auth">
        <div className="py-20 text-center">
          <PressEyebrow className="justify-center">Manuscript not found</PressEyebrow>
        </div>
      </AppShell>
    );
  }

  const catalogEntry = catalogState.catalogEntry;
  const hasDemoPaymentBypass = isDemoPaymentBypassEmail(user?.email);
  const productionAction = nextProductionAction(project, blockingFailures.length, hasDemoPaymentBypass);
  const readiness = readinessFor(project, latestStages);
  const totalWords = resolvedWordCount(metadata, chapterState.chapters);
  const chapterCount = resolvedChapterCount(metadata, chapterState.chapters);
  const releaseBlocked = project.state === "ERROR" || blockingFailures.length > 0;
  const packageReady = project.state === "PACKAGED" || project.state === "PUBLISHING" || project.state === "PUBLISHED";

  const startPublishingCheckout = async () => {
    setCheckoutLoading(true);
    setCheckoutError(null);
    try {
      const baseUrl = window.location.origin;
      const successUrl = `${baseUrl}/project/${project.id}?checkout=success`;
      if (hasDemoPaymentBypass) {
        await recordDemoPublishingBypass(project.id, project.state);
        window.location.assign(successUrl);
        return;
      }

      const createCheckout = httpsCallable<
        { project_id: string; tier: "publishing_standard"; success_url: string; cancel_url: string },
        { checkout_url: string; demo_bypass?: boolean }
      >(functions, "createCheckoutSession");
      const result = await createCheckout({
        project_id: project.id,
        tier: "publishing_standard",
        success_url: successUrl,
        cancel_url: `${baseUrl}/project/${project.id}?checkout=cancelled`,
      });
      window.location.assign(result.data.checkout_url);
    } catch (err) {
      setCheckoutError(err instanceof Error ? err.message : "Checkout failed.");
      setCheckoutLoading(false);
    }
  };

  return (
    <AppShell variant="auth">
      <div className="mx-auto w-full max-w-[1180px] px-4 py-5 sm:px-5 lg:px-6">
        <header className="mb-5 grid gap-4 border-b border-rule pb-5 lg:grid-cols-[1fr_auto] lg:items-end">
          <div className="max-w-3xl">
            <PressEyebrow>Production board / {project.title}</PressEyebrow>
            <h1 className="mt-2 font-display text-[clamp(1.9rem,3vw,3.15rem)] font-semibold leading-[1.02] tracking-as-tight text-ink-1">
              The book's path to release
            </h1>
            <p className="mt-3 max-w-2xl font-ui text-sm leading-as-read text-ink-3">
              Track the manuscript, gates, blockers, release packet, and final release decision in one place.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 lg:justify-end">
            <StatusPill tone={statusTone(project.state)}>{statusLabel(project.state)}</StatusPill>
            <span className="rounded-as-2 border border-rule bg-paper-raised px-3 py-2 font-mono text-[10px] uppercase tracking-as-eyebrow text-ink-3">
              Updated {formatDate(toDate(project.updated_at))}
            </span>
            <Link to={`/project/${project.id}/studio`}>
              <Button type="button" variant="secondary" size="sm">Open source room</Button>
            </Link>
          </div>
        </header>

        <PipelineRibbon latestStages={latestStages} readiness={readiness} />

        <div className="grid gap-4 xl:grid-cols-[220px_minmax(0,1fr)_280px]">
          <aside className="order-2 space-y-3 xl:order-1">
            <PressPanel className="p-4">
              <div className="flex items-center justify-between">
              <PressEyebrow>Production pipe</PressEyebrow>
                <span className="font-mono text-[10px] uppercase tracking-as-eyebrow text-ink-4">
                    {PIPELINE_DEFINITIONS.length} gates
                </span>
              </div>
              <div className="mt-4">
                {PIPELINE_DEFINITIONS.map((definition, index) => {
                  const stage = latestStages.get(definition.stage) ?? null;
                  return (
                    <TimelineItem
                      key={definition.stage}
                      number={String(index + 1)}
                      label={definition.label}
                      status={stage ? labelForStageStatus(stage.status) : "Not started"}
                      statusTone={toneForStage(stage?.status ?? "pending")}
                      last={index === PIPELINE_DEFINITIONS.length - 1}
                    />
                  );
                })}
              </div>
              {pipelineState.error && <InlineError message={pipelineState.error} />}
            </PressPanel>

            <PressPanel className="p-4">
              <PressEyebrow>Readiness</PressEyebrow>
              <div className="mt-4 flex items-end justify-between gap-3">
                <div>
                  <div className="font-display text-4xl font-semibold leading-none tracking-as-tight text-ink-1">{readiness}%</div>
                  <p className="mt-2 font-ui text-sm leading-as-ui text-ink-3">
                    {readiness === 100 ? "Published." : packageReady ? "Release packet generated." : "Book run in progress."}
                  </p>
                </div>
                <div className="flex h-20 w-3 items-end overflow-hidden rounded-full bg-paper-2">
                  <div className="w-full rounded-full bg-[linear-gradient(180deg,var(--as-verify),var(--as-walnut))]" style={{ height: `${readiness}%` }} />
                </div>
              </div>
              <Link to={`/project/${project.id}/review`} className="mt-4 block">
                <Button variant="secondary" size="sm" className="w-full">Open proof room</Button>
              </Link>
            </PressPanel>
          </aside>

          <main className="order-1 space-y-4 xl:order-2">
            <PressPanel className="overflow-hidden p-0">
              <div className="grid lg:grid-cols-[170px_1fr]">
                <div className="border-b border-rule bg-paper p-5 lg:border-b-0 lg:border-r">
                  <BookCover title={project.title} subtitle={labelForGenre(project.settings.genre)} author={project.author_name} tone="mountain" className="mx-auto w-28 sm:w-32" />
                </div>
                <div className="p-5">
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusPill tone={releaseBlocked ? "blocked" : packageReady ? "ready" : "progress"}>
                      {releaseBlocked ? "Blocked" : packageReady ? "Packet ready" : "In production"}
                    </StatusPill>
                    <span className="font-mono text-[10px] uppercase tracking-as-eyebrow text-ink-4">{packageName(project.pricing_tier)}</span>
                  </div>
                  <h2 className="mt-3 font-display text-[clamp(1.8rem,3vw,2.9rem)] font-semibold leading-[1.02] tracking-as-snug text-ink-1">
                    {project.title}
                  </h2>
                  <p className="mt-3 max-w-2xl font-ui text-sm leading-as-read text-ink-2">
                    {descriptionFor(project, metadata, catalogEntry)}
                  </p>
                  <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                    <MetricTile label="Words" value={totalWords === null ? "Not shaped yet" : totalWords.toLocaleString()} />
                    <MetricTile label="Target" value={project.settings.target_word_count?.toLocaleString() ?? "Not set"} />
                    <MetricTile label="Trim" value={catalogEntry?.trim_size ?? metadata?.trim_size ?? project.settings.trim_size} />
                    <MetricTile label="ISBN" value={resolvedIsbn(project, metadata, catalogEntry) ?? "Pending"} tone={resolvedIsbn(project, metadata, catalogEntry) ? "ready" : "blocked"} />
                  </div>
                  <div className="mt-5 flex flex-wrap gap-2">
                    <Link to={`/project/${project.id}/studio`}>
                      <Button variant="walnut" size="sm">Open source room</Button>
                    </Link>
                    <Link to={`/project/${project.id}/review`}>
                      <Button variant="secondary" size="sm">Open proof room</Button>
                    </Link>
                  </div>
                </div>
              </div>
            </PressPanel>

            <PressPanel className="overflow-hidden p-0">
              <div className="flex flex-col gap-2 border-b border-rule px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <PressEyebrow>Release packet</PressEyebrow>
                  <p className="mt-1 font-ui text-sm text-ink-3">
                    The files and book details created by the production run.
                  </p>
                </div>
                <Link to={`/project/${project.id}/review`}>
                  <Button variant="secondary" size="sm">Open proof</Button>
                </Link>
              </div>
              {artifactUrlError && <div className="px-4 py-3"><InlineError message={artifactUrlError} /></div>}
              {metadataError && <div className="px-4 py-3"><InlineError message={metadataError} /></div>}
              {packetArtifacts.length > 0 ? (
                <div className="divide-y divide-rule-faint">
                  {packetArtifacts.map((packet) => (
                    <PacketRow
                      key={packet.key}
                      packet={packet}
                      url={artifactUrls[packet.key] ?? null}
                    />
                  ))}
                </div>
              ) : (
                <EmptyPanel message="No release files have been created for this book yet." />
              )}
            </PressPanel>

            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_270px]">
              <PressPanel className="p-4">
                <PressEyebrow>Quality gates</PressEyebrow>
                {verificationResults.length > 0 ? (
                  <div className="mt-3 divide-y divide-rule-faint">
                    {verificationResults.map((result) => (
                      <GateRow key={`${result.check_id}:${result.description}`} result={result} />
                    ))}
                  </div>
                ) : (
                  <EmptyPanel message="No quality gate results have been recorded yet." compact />
                )}
              </PressPanel>

              <PressPanel className="bg-[linear-gradient(180deg,var(--as-paper-raised),var(--as-paper))] p-4">
                  <PressEyebrow>Book specs</PressEyebrow>
                <div className="mt-4 space-y-3">
                  <Detail label="Genre" value={labelForGenre(project.settings.genre)} />
                  <Detail label="Paper" value={catalogEntry?.paper_stock ?? metadata?.paper_stock ?? project.settings.paper_stock} />
                  <Detail label="Pages" value={catalogEntry?.page_count ? catalogEntry.page_count.toLocaleString() : metadata?.page_count?.toLocaleString() ?? "Not generated"} />
                  <Detail label="Chapters" value={chapterCount === null ? "Not generated" : chapterCount.toLocaleString()} />
                  <Detail label="Retail price" value={catalogEntry?.retail_price ? currency(catalogEntry.retail_price) : metadata?.retail_price_usd ? currency(metadata.retail_price_usd) : "Not generated"} />
                </div>
              </PressPanel>
            </div>

            <PressPanel className="p-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <PressEyebrow>Distribution</PressEyebrow>
                  <p className="mt-1 font-ui text-sm text-ink-3">
                    Distribution appears after the catalog record is created.
                  </p>
                </div>
                <StatusPill tone={catalogEntry ? "ready" : "progress"}>{catalogEntry ? "Catalog created" : "Not released"}</StatusPill>
              </div>
              {catalogEntry ? (
                <div className="mt-3 divide-y divide-rule-faint">
                  {catalogEntry.distribution_channels.map((channel) => (
                    <DestinationRow key={channel} name={CHANNEL_LABELS[channel]} state="Catalog active" />
                  ))}
                </div>
              ) : (
                <EmptyPanel message="No catalog or retailer distribution record exists yet." compact />
              )}
            </PressPanel>

            <PressPanel className="p-4">
              <PressEyebrow>Book details</PressEyebrow>
              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                <Detail label="Title" value={catalogEntry?.title ?? metadata?.title ?? project.title} />
                <Detail label="Author" value={catalogEntry?.author_name ?? metadata?.author_name ?? project.author_name} />
                <Detail label="BISAC" value={formatList(catalogEntry?.bisac_codes ?? metadata?.bisac_codes) ?? "Not generated"} />
                <Detail label="Keywords" value={formatList(catalogEntry?.keywords ?? metadata?.keywords) ?? "Not generated"} />
                <Detail label="Publication" value={formatPublicationDate(catalogEntry, metadata)} />
              </div>
            </PressPanel>
          </main>

          <aside className="order-3 space-y-3">
            <PressPanel className="border-walnut-soft/60 p-4">
              <PressEyebrow>Next decision</PressEyebrow>
              <h2 className="mt-3 font-display text-2xl font-semibold leading-tight text-ink-1">
                {productionAction.title}
              </h2>
              <p className="mt-2 font-ui text-sm leading-as-read text-ink-3">
                {productionAction.body}
              </p>
              {checkoutError && (
                <div className="mt-4 rounded-as-2 border border-flag bg-flag-tint p-3 font-mono text-[10px] uppercase tracking-as-eyebrow text-flag">
                  {checkoutError}
                </div>
              )}
              <Button
                onClick={startPublishingCheckout}
                variant="walnut"
                disabled={checkoutLoading || productionAction.disabled}
                className="mt-4 w-full"
              >
                <CreditCard size={16} className="mr-2" />
                {checkoutLoading ? "Opening checkout..." : productionAction.buttonLabel}
              </Button>
              <Link to={`/project/${project.id}/review`} className="mt-2 block">
                <Button variant="secondary" size="sm" className="w-full"><FileText size={15} className="mr-1" />Open proof room</Button>
              </Link>
            </PressPanel>

            <PressPanel className="p-4">
              <div className="flex items-center justify-between">
                <PressEyebrow>Blockers</PressEyebrow>
                <StatusPill tone={blockingFailures.length > 0 || project.state === "ERROR" ? "blocked" : "ready"}>
                  {project.state === "ERROR" ? blockingFailures.length + 1 : blockingFailures.length}
                </StatusPill>
              </div>
              {project.state === "ERROR" && (
                <BlockerItem label={project.error_stage ?? "Production error"} body={project.error_message ?? "The book run is blocked."} />
              )}
              {blockingFailures.map((failure) => (
                <BlockerItem key={`${failure.check_id}:${failure.description}`} label={failure.check_id} body={failure.error_message ?? failure.description} />
              ))}
              {project.state !== "ERROR" && blockingFailures.length === 0 && (
                <p className="mt-4 font-ui text-sm text-ink-3">No blockers are holding the book back.</p>
              )}
            </PressPanel>

            <PressPanel className="p-4">
              <PressEyebrow>Warnings</PressEyebrow>
              {warnings.length > 0 ? (
                <div className="mt-4 space-y-3">
                  {warnings.map((warning) => (
                    <WarningItem key={`${warning.check_id}:${warning.description}`} label={warning.check_id} body={warning.error_message ?? warning.description} />
                  ))}
                </div>
              ) : (
                <p className="mt-4 font-ui text-sm text-ink-3">No warnings are recorded.</p>
              )}
            </PressPanel>

            <PressPanel className="p-4">
              <PressEyebrow>Activity</PressEyebrow>
              {stateLogState.entries.length > 0 ? (
                <div className="mt-4 space-y-4">
                  {stateLogState.entries.slice(0, 5).map((entry) => (
                    <Activity key={entry.id} icon={<CalendarBlank size={16} />} entry={entry} />
                  ))}
                </div>
              ) : (
                <EmptyPanel message="No production movement has been recorded yet." compact />
              )}
              {stateLogState.error && <InlineError message={stateLogState.error} />}
            </PressPanel>
          </aside>
        </div>
      </div>
    </AppShell>
  );
}

function PipelineRibbon({
  latestStages,
  readiness,
}: {
  latestStages: ReadonlyMap<PipelineStageName, PipelineStage>;
  readiness: number;
}) {
  return (
    <PressPanel className="mb-4 p-4">
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <PressEyebrow>Production spine</PressEyebrow>
          <p className="mt-1 font-ui text-sm text-ink-3">{readiness}% ready across source, manuscript, proof, packet, and release.</p>
        </div>
        <StatusPill tone={readiness >= 94 ? "ready" : readiness >= 56 ? "editing" : "progress"}>{readiness}% ready</StatusPill>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-9">
        {PIPELINE_DEFINITIONS.map((definition) => {
          const stage = latestStages.get(definition.stage) ?? null;
          const tone = toneForStage(stage?.status ?? "pending");
          return (
            <div key={definition.stage} className={`rounded-as-2 border px-3 py-2 ${tone === "complete" ? "border-verify-soft/40 bg-verify-tint" : tone === "active" ? "border-walnut bg-walnut-tint" : tone === "blocked" ? "border-flag/30 bg-flag-tint" : "border-rule bg-paper"}`}>
              <p className={`truncate font-ui text-sm font-semibold ${tone === "complete" ? "text-verify" : tone === "active" ? "text-walnut-deep" : tone === "blocked" ? "text-flag" : "text-ink-3"}`}>{definition.label}</p>
              <p className="mt-1 truncate font-mono text-[9px] uppercase tracking-as-eyebrow text-ink-4">
                {stage ? labelForStageStatus(stage.status) : "Not started"}
              </p>
            </div>
          );
        })}
      </div>
    </PressPanel>
  );
}

function TimelineItem({
  number,
  label,
  status,
  statusTone,
  last = false,
}: {
  number: string;
  label: string;
  status: string;
  statusTone: "complete" | "active" | "blocked" | "pending";
  last?: boolean;
}) {
  const numberClass = {
    complete: "bg-verify text-paper-raised",
    active: "bg-walnut text-paper-raised",
    blocked: "bg-flag text-paper-raised",
    pending: "border border-rule-strong bg-paper text-ink-3",
  }[statusTone];

  return (
    <div className="grid grid-cols-[24px_1fr] gap-3">
      <div className="flex flex-col items-center">
        <span className={`grid h-6 w-6 place-items-center rounded-full font-mono text-[10px] ${numberClass}`}>{number}</span>
        {!last && <span className="my-2 h-5 w-px bg-rule" />}
      </div>
      <div className="min-w-0 pb-3">
        <p className={`font-ui text-sm font-semibold ${statusTone === "active" ? "text-walnut-deep" : statusTone === "blocked" ? "text-flag" : "text-ink-1"}`}>{label}</p>
        <p className="mt-0.5 font-mono text-[9px] uppercase tracking-as-eyebrow text-ink-4">{status}</p>
      </div>
    </div>
  );
}

function MetricTile({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: string;
  tone?: "neutral" | "ready" | "blocked";
}) {
  const toneClass = tone === "ready" ? "text-verify" : tone === "blocked" ? "text-flag" : "text-ink-1";

  return (
    <div className="rounded-as-2 border border-rule bg-paper px-3 py-2">
      <p className="font-mono text-[9px] uppercase tracking-as-eyebrow text-ink-4">{label}</p>
      <p className={`mt-1 break-words font-ui text-sm font-semibold ${toneClass}`}>{value}</p>
    </div>
  );
}

function PacketRow({
  packet,
  url,
}: {
  packet: PacketArtifact;
  url: string | null;
}) {
  const copy = ARTIFACT_COPY[packet.type];
  const version = typeof packet.artifact.version === "number" ? `v${packet.artifact.version}` : packet.stage.status;
  const date = formatDate(toDate(packet.artifact.created_at) ?? toDate(packet.stage.completed_at) ?? toDate(packet.stage.started_at));
  const size = typeof packet.artifact.size_bytes === "number" && packet.artifact.size_bytes > 0 ? formatBytes(packet.artifact.size_bytes) : null;

  return (
    <div className="grid gap-3 px-4 py-3 sm:grid-cols-[28px_1fr_150px_88px] sm:items-center">
      <FileText size={17} className="text-walnut" />
      <div className="min-w-0">
        <p className="font-ui text-sm font-semibold text-ink-1">{copy.name}</p>
        <p className="font-ui text-sm text-ink-3">{copy.purpose}</p>
      </div>
      <p className="font-mono text-[10px] uppercase tracking-as-eyebrow text-ink-4">
        {version} · {date}{size ? ` · ${size}` : ""}
      </p>
      {url ? (
        <a href={url} target="_blank" rel="noreferrer">
          <Button variant="secondary" size="sm" className="w-full">
            Open <ArrowSquareOut size={14} className="ml-1" />
          </Button>
        </a>
      ) : (
        <Button variant="secondary" size="sm" className="w-full" disabled>Open</Button>
      )}
    </div>
  );
}

function GateRow({ result }: { result: VerificationResult }) {
  const tone = result.passed ? "text-verify" : result.blocking ? "text-flag" : "text-walnut-deep";
  const label = result.passed ? "Complete" : result.blocking ? "Blocking" : "Warning";

  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <div>
        <span className="font-ui text-sm text-ink-2">{result.description}</span>
        {result.error_message && <p className="mt-1 font-ui text-xs leading-as-ui text-flag">{result.error_message}</p>}
      </div>
      <span className={`shrink-0 font-mono text-[9px] uppercase tracking-as-eyebrow ${tone}`}>{label}</span>
    </div>
  );
}

function DestinationRow({ name, state }: { name: string; state: string }) {
  return (
    <div className="grid gap-2 py-3 sm:grid-cols-[1fr_auto] sm:items-center">
      <div>
        <p className="font-ui text-sm font-semibold text-ink-1">{name}</p>
        <p className="font-ui text-sm text-ink-3">{state}</p>
      </div>
      <StatusPill tone="ready">Ready</StatusPill>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="font-mono text-[9px] uppercase tracking-as-eyebrow text-ink-4">{label}</p>
      <p className="mt-1 font-ui text-sm leading-as-ui text-ink-1">{value}</p>
    </div>
  );
}

function BlockerItem({ label, body }: { label: string; body: string }) {
  return (
    <div className="mt-4 border-l border-flag pl-3">
      <div className="flex gap-2">
        <WarningCircle size={18} className="mt-0.5 shrink-0 text-flag" />
        <div>
          <p className="font-ui text-sm font-semibold text-flag">{label}</p>
          <p className="mt-1 font-ui text-sm leading-as-read text-ink-3">{body}</p>
        </div>
      </div>
    </div>
  );
}

function WarningItem({ label, body }: { label: string; body: string }) {
  return (
    <div className="border-l border-rule pl-3">
      <p className="font-ui text-sm font-semibold text-ink-1">{label}</p>
      <p className="mt-1 font-ui text-sm leading-as-ui text-ink-3">{body}</p>
    </div>
  );
}

function Activity({ icon, entry }: { icon: ReactNode; entry: StateTransitionLog }) {
  return (
    <div className="grid grid-cols-[20px_1fr] gap-3">
      <div className="mt-1 text-ink-4">{icon}</div>
      <div>
        <p className="font-ui text-sm font-semibold text-ink-1">{entry.trigger}</p>
        <p className="font-mono text-[9px] uppercase tracking-as-eyebrow text-ink-4">
          {entry.from_state} → {entry.to_state} · {formatDate(toDate(entry.timestamp))}
        </p>
      </div>
    </div>
  );
}

function EmptyPanel({ message, compact = false }: { message: string; compact?: boolean }) {
  return (
    <div className={compact ? "mt-3 rounded-as-2 border border-rule bg-paper p-3" : "p-4"}>
      <p className="font-ui text-sm leading-as-read text-ink-3">{message}</p>
    </div>
  );
}

function InlineError({ message }: { message: string }) {
  return (
    <div className="mt-3 rounded-as-2 border border-flag bg-flag-tint p-3 font-mono text-[10px] uppercase tracking-as-eyebrow text-flag">
      {message}
    </div>
  );
}

function buildLatestStageMap(stages: readonly PipelineStage[]): ReadonlyMap<PipelineStageName, PipelineStage> {
  const map = new Map<PipelineStageName, PipelineStage>();
  for (const stage of stages) {
    const current = map.get(stage.stage);
    if (!current || stageTime(stage) >= stageTime(current)) {
      map.set(stage.stage, stage);
    }
  }
  return map;
}

function buildPacketArtifacts(stages: readonly PipelineStage[]): PacketArtifact[] {
  const latestByType = new Map<DisplayArtifactType, PacketArtifact>();
  for (const stage of stages) {
    for (const artifact of stage.artifact_refs ?? []) {
      const type = packetArtifactType(artifact.type);
      if (!type) continue;
      const key = artifactKey(artifact, stage);
      const packet: PacketArtifact = { key, artifact, stage, type };
      const current = latestByType.get(type);
      if (!current || artifactTime(packet) >= artifactTime(current)) {
        latestByType.set(type, packet);
      }
    }
  }
  return Array.from(latestByType.values()).sort((a, b) => artifactSortIndex(a.type) - artifactSortIndex(b.type));
}

function buildVerificationResults(stages: readonly PipelineStage[]): VerificationResult[] {
  const results = stages.flatMap((stage) => stage.verification_results ?? []);
  return results.sort((a, b) => {
    const left = toDate(a.checked_at)?.getTime() ?? 0;
    const right = toDate(b.checked_at)?.getTime() ?? 0;
    return right - left;
  });
}

function packetArtifactType(type: ArtifactType): DisplayArtifactType | null {
  if (type in ARTIFACT_COPY) return type as DisplayArtifactType;
  return null;
}

function artifactSortIndex(type: DisplayArtifactType): number {
  const order: readonly DisplayArtifactType[] = [
    "draft_docx",
    "revised_docx",
    "editorial_output",
    "editorial_report",
    "continuity_report",
    "interior_pdf",
    "cover_pdf",
    "epub",
    "onix_xml",
    "amazon_listing",
    "metadata_json",
    "kdp_export",
    "ingramspark_bundle",
  ];
  return order.indexOf(type);
}

function artifactKey(artifact: ArtifactRef, stage: PipelineStage): string {
  const id = artifact.artifact_id || `${artifact.type}:${artifact.storage_path}`;
  return `${stage.id}:${id}`;
}

function artifactDownloadPath(packet: PacketArtifact): string {
  if (packet.type === "kdp_export" || packet.type === "ingramspark_bundle") {
    return packet.artifact.storage_path.endsWith("/")
      ? `${packet.artifact.storage_path}manifest.json`
      : `${packet.artifact.storage_path}/manifest.json`;
  }
  return packet.artifact.storage_path;
}

function artifactTime(packet: PacketArtifact): number {
  return Math.max(
    toDate(packet.artifact.created_at)?.getTime() ?? 0,
    toDate(packet.stage.completed_at)?.getTime() ?? 0,
    toDate(packet.stage.started_at)?.getTime() ?? 0,
    typeof packet.artifact.version === "number" ? packet.artifact.version : 0,
  );
}

function stageTime(stage: PipelineStage): number {
  return Math.max(
    toDate(stage.completed_at)?.getTime() ?? 0,
    toDate(stage.started_at)?.getTime() ?? 0,
  );
}

async function getBytesJson(path: string): Promise<unknown> {
  const bytes = await getBytes(storageRef(storage, path));
  return JSON.parse(new TextDecoder().decode(bytes)) as unknown;
}

function parseProductionMetadata(value: unknown): ProductionMetadata {
  if (!isRecord(value)) return {};
  return {
    title: stringValue(value.title),
    subtitle: stringValue(value.subtitle),
    author_name: stringValue(value.author_name),
    isbn: normalizedIsbn(stringValue(value.isbn)) ?? undefined,
    trim_size: stringValue(value.trim_size),
    paper_stock: stringValue(value.paper_stock),
    page_count: numberValue(value.page_count),
    word_count: numberValue(value.word_count),
    chapter_count: numberValue(value.chapter_count),
    spine_width_inches: numberValue(value.spine_width_inches),
    cover_width_inches: numberValue(value.cover_width_inches),
    cover_height_inches: numberValue(value.cover_height_inches),
    retail_price_usd: numberValue(value.retail_price_usd),
    bisac_codes: stringArray(value.bisac_codes),
    keywords: stringArray(value.keywords),
    publication_date: stringValue(value.publication_date),
    description: stringValue(value.description),
  };
}

function resolvedWordCount(metadata: ProductionMetadata | null, chapters: readonly Chapter[]): number | null {
  if (typeof metadata?.word_count === "number" && metadata.word_count > 0) return metadata.word_count;
  const chapterWords = chapters.reduce((sum, chapter) => sum + Math.max(0, chapter.word_count), 0);
  return chapterWords > 0 ? chapterWords : null;
}

function resolvedChapterCount(metadata: ProductionMetadata | null, chapters: readonly Chapter[]): number | null {
  if (typeof metadata?.chapter_count === "number" && metadata.chapter_count > 0) return metadata.chapter_count;
  return chapters.length > 0 ? chapters.length : null;
}

function resolvedIsbn(project: Project, metadata: ProductionMetadata | null, catalog: CatalogEntry | null): string | null {
  return normalizedIsbn(project.isbn) ?? normalizedIsbn(catalog?.isbn) ?? normalizedIsbn(metadata?.isbn);
}

function normalizedIsbn(value: string | undefined): string | null {
  if (!value || value.toUpperCase() === "PENDING") return null;
  return value;
}

function descriptionFor(project: Project, metadata: ProductionMetadata | null, catalog: CatalogEntry | null): string {
  const catalogDescription = stripped(catalog?.description);
  if (catalogDescription) return catalogDescription;
  const metadataDescription = stripped(metadata?.description);
  if (metadataDescription) return metadataDescription;
  if (project.state === "ERROR") return project.error_message ?? "The book needs operator attention before it can continue.";
  return `${labelForGenre(project.settings.genre)} manuscript by ${project.author_name}. The production record fills in as PullPress shapes the book.`;
}

function nextProductionAction(project: Project, blockingFailureCount: number, demoBypass: boolean): ProductionAction {
  const hasPublishingPayment = project.paid_tiers?.some((tier) => tier.startsWith("publishing_")) ?? false;
  if (project.state === "PUBLISHED") {
    return {
      title: "The book is published.",
      body: "The catalog record and release outputs have been created.",
      buttonLabel: "Published",
      disabled: true,
    };
  }
  if (project.state === "ERROR" || blockingFailureCount > 0) {
    return {
      title: "Clear the blockers first.",
      body: "Release checkout is disabled while the book has blocking verification failures.",
      buttonLabel: "Blocked",
      disabled: true,
    };
  }
  if (project.state === "PUBLISHING") {
    return {
      title: "Release is underway.",
      body: "The book is already in the release workflow.",
      buttonLabel: "Releasing",
      disabled: true,
    };
  }
  if (project.state !== "PACKAGED") {
    return {
      title: "Finish the release packet.",
      body: "Checkout opens after packaging creates the final deliverables.",
      buttonLabel: "Not ready",
      disabled: true,
    };
  }
  if (hasPublishingPayment) {
    return {
      title: "Release payment recorded.",
      body: "The book is ready to move into the release stage.",
      buttonLabel: "Paid",
      disabled: true,
    };
  }
  if (demoBypass) {
    return {
      title: project.isbn ? "Start demo release." : "Start demo release and assign ISBN.",
      body: project.isbn
        ? "This demo account can start release without Stripe checkout."
        : "This demo account can skip Stripe and assign an ISBN from operator inventory.",
      buttonLabel: "Start demo release",
      disabled: false,
    };
  }
  return {
    title: project.isbn ? "Approve release checkout." : "Approve release and assign ISBN.",
    body: project.isbn
      ? "The packet is ready. Checkout records the release payment and starts the release workflow."
      : "The release workflow assigns an ISBN from operator inventory after payment is verified.",
    buttonLabel: "Approve release",
    disabled: false,
  };
}

async function recordDemoPublishingBypass(projectId: string, currentState: StateMachineState): Promise<void> {
  if (currentState !== "PACKAGED") {
    throw new Error(`Demo publishing bypass expected PACKAGED, got ${currentState}`);
  }

  const batch = writeBatch(db);
  const projectRef = doc(db, "projects", projectId);
  const logRef = doc(collection(db, "projects", projectId, "state_log"));

  batch.update(projectRef, {
    payment_status: "paid",
    paid_tiers: arrayUnion("publishing_standard"),
    demo_payment_bypass: true,
    state: "PUBLISHING",
    status: "active",
    updated_at: serverTimestamp(),
  });
  batch.set(logRef, {
    from_state: "PACKAGED",
    to_state: "PUBLISHING",
    trigger: `Demo payment bypass for ${DEMO_PAYMENT_BYPASS_EMAIL}`,
    timestamp: serverTimestamp(),
    metadata: { tier: "publishing_standard", demo_bypass: true },
  });

  await batch.commit();
}

function readinessFor(project: Project, latestStages: ReadonlyMap<PipelineStageName, PipelineStage>): number {
  if (latestStages.size === 0) return STATE_READINESS[project.state];
  const completed = PIPELINE_DEFINITIONS.filter((definition) => latestStages.get(definition.stage)?.status === "completed").length;
  const pipelineReadiness = Math.round((completed / PIPELINE_DEFINITIONS.length) * 100);
  return Math.max(STATE_READINESS[project.state], pipelineReadiness);
}

function toneForStage(status: StageStatus): "complete" | "active" | "blocked" | "pending" {
  if (status === "completed" || status === "skipped") return "complete";
  if (status === "running" || status === "retrying") return "active";
  if (status === "failed") return "blocked";
  return "pending";
}

function labelForStageStatus(status: StageStatus): string {
  const labels: Record<StageStatus, string> = {
    pending: "Pending",
    running: "Running",
    completed: "Complete",
    failed: "Failed",
    retrying: "Retrying",
    skipped: "Skipped",
  };
  return labels[status];
}

function statusTone(state: StateMachineState): "progress" | "ready" | "published" | "blocked" {
  if (state === "PUBLISHED") return "published";
  if (state === "PACKAGED" || state === "PUBLISHING") return "ready";
  if (state === "ERROR") return "blocked";
  return "progress";
}

function statusLabel(state: StateMachineState): string {
  if (state === "PUBLISHED") return "Published";
  if (state === "PUBLISHING") return "Releasing";
  if (state === "PACKAGED") return "Packet ready";
  if (state === "ERROR") return "Blocked";
  return "In production";
}

function packageName(tier: Project["pricing_tier"]): string {
  const labels: Record<Project["pricing_tier"], string> = {
    production: "Production path",
    publishing: "Release path",
    premium: "Premium package",
  };
  return labels[tier];
}

function labelForGenre(genre: Project["settings"]["genre"]): string {
  return genre.replace("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatPublicationDate(catalog: CatalogEntry | null, metadata: ProductionMetadata | null): string {
  const catalogDate = toDate(catalog?.publication_date);
  if (catalogDate) return formatDate(catalogDate);
  if (metadata?.publication_date) return metadata.publication_date;
  return "Not released";
}

function formatDate(date: Date | null): string {
  if (!date) return "Not recorded";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function formatList(values: readonly string[] | undefined): string | null {
  if (!values || values.length === 0) return null;
  return values.join(", ");
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function currency(value: number): string {
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(value);
}

function stripped(value: string | undefined): string | null {
  const text = value?.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  return text && text.length > 0 ? text : null;
}

function toDate(timestamp: { toDate: () => Date } | undefined | null): Date | null {
  if (!timestamp) return null;
  return timestamp.toDate();
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function stringValue(value: unknown): string | undefined {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : undefined;
}

function numberValue(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function stringArray(value: unknown): readonly string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const strings = value.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
  return strings.length > 0 ? strings : undefined;
}
